import { Dispatch, SetStateAction, useState, useImperativeHandle, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Resettable } from '@/models/Resettable';
import { FilterState } from '../FiltersArea';
import OptionsRangeFilter from '@/components/filters/OptionsRangeFilter/OptionsRangeFilter';

interface PaymentMethodFilterProps {
  setFilterState: Dispatch<SetStateAction<FilterState>>;
  ref?: Ref<Resettable>;
}

const PaymentMethodFilter = ({ setFilterState, ref }: PaymentMethodFilterProps) => {
  const { t } = useTranslation();
  const [checkedOptions, setCheckedOptions] = useState<Set<string>>(new Set());

  const options = [
    {
      id: 'creditCard',
      label: t('creditCard'),
      checked: checkedOptions.has('creditCard'),
      paymentMethod: ['creditCard'],
    },
    {
      id: 'directDebit',
      label: t('directDebit'),
      checked: checkedOptions.has('directDebit'),
      paymentMethod: ['directDebit'],
    },
    {
      id: 'bankTransfer',
      label: t('bankTransfer'),
      checked: checkedOptions.has('bankTransfer'),
      paymentMethod: ['bankTransfer', 'bankTransferFromAbroad'],
    },
    {
      id: 'checks',
      label: t('checks'),
      checked: checkedOptions.has('checks'),
      paymentMethod: ['checks', 'matach'],
    },
    {
      id: 'cash',
      label: t('cash'),
      checked: checkedOptions.has('cash'),
      paymentMethod: ['cash'],
    },
    {
      id: 'bit',
      label: t('bit'),
      checked: checkedOptions.has('bit'),
      paymentMethod: ['bit'],
    },
    {
      id: 'paypal',
      label: t('paypal'),
      checked: checkedOptions.has('paypal'),
      paymentMethod: ['paypal'],
    },
    {
      id: 'other',
      label: t('other'),
      checked: checkedOptions.has('other'),
      paymentMethod: ['other', 'postalBank', 'equivalentValue'],
    },
  ];

  const handleOptionChange = (optionId: string) => {
    const newCheckedOptions = new Set(checkedOptions);
    if (newCheckedOptions.has(optionId)) {
      newCheckedOptions.delete(optionId);
    } else {
      newCheckedOptions.add(optionId);
    }
    setCheckedOptions(newCheckedOptions);

    const selectedValues = options.filter((opt) => newCheckedOptions.has(opt.id)).flatMap((opt) => opt.paymentMethod);

    setFilterState((prev) => ({
      ...prev,
      paymentMethods: selectedValues,
    }));
  };

  const handleSelectAllChange = (selectAll: boolean) => {
    const newCheckedOptions = selectAll ? new Set(options.map((option) => option.id)) : new Set<string>();
    setCheckedOptions(newCheckedOptions);

    const selectedValues = selectAll ? options.flatMap((opt) => opt.paymentMethod) : [];

    setFilterState((prev) => ({
      ...prev,
      paymentMethods: selectedValues,
    }));
  };

  useImperativeHandle(ref, () => ({
    reset: () => {
      setCheckedOptions(new Set());
    },
  }));

  return (
    <OptionsRangeFilter
      title="paymentMethod"
      filterType="options"
      options={options.map(({ id, label, checked }) => ({ id, label, checked }))}
      onOptionChange={handleOptionChange}
      onSelectAllChange={handleSelectAllChange}
      selectionType="checkbox"
    />
  );
};

export default PaymentMethodFilter;
